import { useEffect } from "react";
import {
  isToolAllowed,
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";

/**
 * Coach Review keyboard shortcuts.
 *
 * Body-attribute scoping: shortcuts fire only when
 * `<body data-coach-review-source>` is set (which the SourceToggle
 * sets on mount + clears on unmount). That keeps the M4 keys from
 * firing on /canvas-demo or any other route that mounts a canvas
 * for its own purposes.
 *
 * Input-focus skip-guard: shortcuts skip when focus is inside
 * `<input>`, `<textarea>`, `<select>`, or `[contenteditable]`. Without
 * this, typing the letter "A" into the composer's title field
 * would arm the arrow tool.
 *
 * Modifier-key skip: any of Cmd / Ctrl / Alt held → skip. This
 * leaves Cmd+S (browser save), Cmd+F (browser find), etc.
 * untouched. Shift is NOT skipped (we use Shift on its own as a
 * "secondary" modifier in legacy code; M4 doesn't need it).
 *
 * Shortcuts (mirror the legacy review.js mapping + the M3 canvas
 * shortcuts):
 *   V → select
 *   A → arrow
 *   L → line
 *   Z → zone
 *   T → label
 *   F → freehand (pen)
 *   P → player          (tactical-only; falls back to select on
 *                        telestrator surface — armTool's surface-
 *                        validity gate handles this silently)
 *   B → ball            (tactical-only; same fallback)
 *   S → spotlight       (telestrator-only)
 *   D → dim             (telestrator-only)
 *   G → formation       (telestrator-only)
 *   Escape → disarm (back to select) + clear selection
 *   ?  → toggle the help popover (parent owns the state)
 *
 * Note on Save shortcut: the legacy code had `S` mapped to "save
 * note." That conflicts with the M3 telestrator's `S` = spotlight.
 * For M4 we keep S → spotlight (consistent with M3 + the on-
 * canvas tool palette) and route save via the submit button only.
 * If a future review brings back save-on-S, replace via a
 * "preferences" prop here rather than hardcoding the rebind.
 */

const TOOL_BINDINGS: Record<string, EditorToolKind> = {
  V: "select",
  A: "arrow",
  L: "line",
  Z: "zone",
  T: "label",
  F: "freehand",
  P: "player",
  B: "ball",
  S: "spotlight",
  D: "dim",
  G: "formation",
};

export type ReviewKeyboardShortcutsOptions = {
  /** When set, pressing `?` calls this. The parent owns the help
   * popover state. */
  onToggleHelp?: () => void;
};

export function useReviewKeyboardShortcuts({
  onToggleHelp,
}: ReviewKeyboardShortcutsOptions = {}) {
  const armTool = useReviewEditorStore((s) => s.armTool);
  const disarm = useReviewEditorStore((s) => s.disarm);
  const surface = useReviewEditorStore((s) => s.surface);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Body-attribute scope: skip if Coach Review isn't the
      // active surface. The SourceToggle sets + clears this.
      if (!document.body.dataset.coachReviewSource) return;

      // Modifier-key skip (browser shortcuts win).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Input-focus skip-guard.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }

      // Help popover: ? triggers regardless of surface.
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        onToggleHelp?.();
        return;
      }

      // Esc → disarm + clear selection (per-surface, via the
      // review-editor store).
      if (e.key === "Escape") {
        // Don't preventDefault — Esc may close other modals too.
        disarm();
        return;
      }

      // Tool shortcuts. Skip if a non-tool key fired.
      const key = e.key.toUpperCase();
      const tool = TOOL_BINDINGS[key];
      if (!tool) return;

      // isToolAllowed gates the tool against the current surface;
      // armTool's own validity check also falls back to select on
      // invalid combinations. We check here too so a key bound to
      // an unavailable tool simply does nothing (visible to the
      // user as "no-op" — they can still type letters elsewhere).
      if (!isToolAllowed(tool, surface)) return;

      e.preventDefault();
      armTool(tool);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [armTool, disarm, surface, onToggleHelp]);
}
